import { AviaPassengerFareFragment } from '@websky/graphql';

import { addMoney, multiplyMoney } from '@shared/lib/money';
import { BuildFareBreakdownParams, FareBreakdown, FareGroupBreakdown, FareSegmentGroup, PassengerFare } from './types';

export const DEFAULT_GROUP_ID = '__default__';

const DEFAULT_GROUP: FareSegmentGroup = { groupId: DEFAULT_GROUP_ID, segmentIds: [] };

const accumulatePassenger = (existing: PassengerFare | undefined, fare: AviaPassengerFareFragment): PassengerFare => ({
	passengerType: fare.passengerType,
	count: existing?.count ?? fare.count,
	baseFare: addMoney(existing?.baseFare, fare.baseFare),
	totalTaxes: addMoney(existing?.totalTaxes, fare.totalTaxes),
	totalFare: addMoney(existing?.totalFare, fare.totalFare)
});

export const buildFareBreakdown = ({
	passengerFares,
	groups,
	total,
	additionalPrices,
	splitPassengers
}: BuildFareBreakdownParams): FareBreakdown => {
	const effectiveGroups: FareSegmentGroup[] = groups?.length ? groups : [DEFAULT_GROUP];

	const segmentToGroup = new Map<string, string>();
	effectiveGroups.forEach(group => {
		group.segmentIds.forEach(segmentId => segmentToGroup.set(segmentId, group.groupId));
	});

	const buckets = new Map<string, Map<string, PassengerFare>>();
	const ensureBucket = (groupId: string) => {
		if (!buckets.has(groupId)) {
			buckets.set(groupId, new Map());
		}

		return buckets.get(groupId)!;
	};

	passengerFares.forEach(fare => {
		if (!fare) {
			return;
		}

		const matchedGroupIds = Array.from(
			new Set(
				(fare.segments ?? [])
					.map(segment => (segment?.id ? segmentToGroup.get(segment.id) : undefined))
					.filter(Boolean)
			)
		);

		const targetGroupIds = matchedGroupIds.length ? matchedGroupIds : [DEFAULT_GROUP_ID];

		targetGroupIds.forEach(groupId => {
			const bucket = ensureBucket(groupId);
			bucket.set(fare.passengerType, accumulatePassenger(bucket.get(fare.passengerType), fare));
		});
	});

	const orderedGroupIds = effectiveGroups.map(group => group.groupId);
	if (buckets.has(DEFAULT_GROUP_ID) && !orderedGroupIds.includes(DEFAULT_GROUP_ID)) {
		orderedGroupIds.push(DEFAULT_GROUP_ID);
	}

	const segmentIdsByGroup = new Map(effectiveGroups.map(group => [group.groupId, group.segmentIds]));

	const toDisplayPassengers = (fares: PassengerFare[]): PassengerFare[] => {
		if (!splitPassengers) {
			return fares.map(fare => ({
				...fare,
				baseFare: multiplyMoney(fare.baseFare, fare.count),
				totalTaxes: multiplyMoney(fare.totalTaxes, fare.count),
				totalFare: multiplyMoney(fare.totalFare, fare.count)
			}));
		}

		return fares.reduce<PassengerFare[]>(
			(acc, fare) => acc.concat(Array.from({ length: fare.count }, () => ({ ...fare, count: 1 }))),
			[]
		);
	};

	const breakdownGroups: FareGroupBreakdown[] = orderedGroupIds
		.map(groupId => ({
			groupId,
			segmentIds: segmentIdsByGroup.get(groupId) ?? [],
			passengers: toDisplayPassengers(Array.from(buckets.get(groupId)?.values() ?? []))
		}))
		.filter(group => group.passengers.length > 0);

	return { groups: breakdownGroups, total, additionalPrices };
};
